/**
 * backgroundJobsService · suscripción a core.background_jobs vía realtime.
 *
 * El Worker agentic dispara jobs async (deep search, generación campaña batch,
 * etc), inserta una fila en core.background_jobs con status='running' y
 * actualiza progress_step a medida que ejecuta. El frontend se subscribe a esa
 * fila por realtime y muestra:
 *   - Toast/sidebar con progreso humano-legible mientras corre.
 *   - Notif al terminar con link al recurso creado (related_prospect_id, etc).
 *
 * Patrón:
 *   const channel = subscribeToJob(jobId, (job) => {
 *     setJob(job);
 *     if (job.status === 'done') navigate(`/prospects/${job.related_prospect_id}`);
 *   });
 *   // ... después de uso ...
 *   channel.unsubscribe();
 */
import type { RealtimeChannel } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';

export type JobStatus = 'pending' | 'running' | 'done' | 'error' | 'cancelled';

export type JobKind =
  | 'analyze_prospect_deep'
  | 'analyze_prospect_deep_v2'
  | 'generate_campaign_deep'
  | 'bulk_prospect_enrichment';

export interface BackgroundJob {
  id: string;
  owner_user_id: string;
  kind: JobKind;
  status: JobStatus;
  input_json: Record<string, unknown>;
  result_json: Record<string, unknown> | null;
  error_json: { message?: string; stack?: string } | null;
  progress_step: string | null;
  progress_pct: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  related_prospect_id: string | null;
  related_campaign_id: string | null;
  /**
   * Marca de descarte del user (UI). NULL = visible en Tray/Bell.
   * Postea con dismissJob(jobId). M17.
   */
  dismissed_at: string | null;
}

/**
 * Lee el estado actual de un job.
 */
export async function getJob(jobId: string): Promise<BackgroundJob | null> {
  const { data, error } = await supabase
    .from('background_jobs')
    .select('*')
    .eq('id', jobId)
    .maybeSingle();
  if (error) {
    console.error('[backgroundJobs] getJob:', error.message);
    return null;
  }
  return data as unknown as BackgroundJob | null;
}

/**
 * Suscribe a UPDATEs de un job específico. El callback se dispara cada vez que
 * el Worker actualiza progress_step / status / result_json.
 *
 * Devuelve el channel — el caller es responsable de llamar channel.unsubscribe()
 * al desmontar.
 *
 * NOTA · channel name lleva suffix random para evitar colisión cuando varios
 * componentes suscriben al mismo job en simultáneo (Supabase realtime no
 * permite `.on()` después de `.subscribe()` en el mismo channel).
 */
export function subscribeToJob(
  jobId: string,
  onUpdate: (job: BackgroundJob) => void,
): RealtimeChannel {
  const suffix = Math.random().toString(36).slice(2, 10);
  return supabase
    .channel(`bg-job-${jobId}-${suffix}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'core',
        table: 'background_jobs',
        filter: `id=eq.${jobId}`,
      },
      (payload) => {
        onUpdate(payload.new as unknown as BackgroundJob);
      },
    )
    .subscribe();
}

/**
 * Lista jobs recientes del usuario (running + últimos 5 terminados).
 * Filtra dismissed_at IS NULL para no mostrar jobs descartados por el user.
 * Útil para el ToastTray global que muestra varios jobs en simultáneo.
 */
export async function listRecentJobs(): Promise<BackgroundJob[]> {
  const { data, error } = await supabase
    .from('background_jobs')
    .select('*')
    .is('dismissed_at', null)
    .order('created_at', { ascending: false })
    .limit(10);
  if (error) {
    console.error('[backgroundJobs] listRecentJobs:', error.message);
    return [];
  }
  return (data as unknown as BackgroundJob[]) ?? [];
}

/**
 * Marca un job como descartado por el user. Persiste en DB para que el job no
 * vuelva a aparecer en Tray/Bell al refrescar. La fila sigue existiendo para
 * auditoría — solo se oculta de la UI.
 */
export async function dismissJob(jobId: string): Promise<void> {
  // database.types.ts está outdated post-M17. Cast escape hasta regenerar.
  const payload = { dismissed_at: new Date().toISOString() } as unknown as never;
  const { error } = await supabase
    .from('background_jobs')
    .update(payload)
    .eq('id', jobId);
  if (error) {
    console.error('[backgroundJobs] dismissJob:', error.message);
    throw new Error(`No se pudo descartar el job: ${error.message}`);
  }
}

/**
 * Suscribe a TODOS los UPDATEs de jobs del usuario actual (filter por
 * owner_user_id via RLS — el realtime respeta las policies del schema core).
 *
 * Útil para el ToastTray global que vive en AppLayout y para la
 * NotificationBell del Topbar — ambos llaman a este helper, por eso el
 * channel name lleva suffix random: Supabase realtime no permite agregar
 * callbacks via `.on()` a un channel ya subscribed, entonces dos componentes
 * compartiendo `bg-jobs-user-${userId}` rompen al montar el segundo.
 */
export function subscribeToUserJobs(
  ownerUserId: string,
  onChange: (job: BackgroundJob) => void,
): RealtimeChannel {
  const suffix = Math.random().toString(36).slice(2, 10);
  return supabase
    .channel(`bg-jobs-user-${ownerUserId}-${suffix}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'core',
        table: 'background_jobs',
        filter: `owner_user_id=eq.${ownerUserId}`,
      },
      (payload) => {
        if (payload.eventType === 'DELETE') return;
        onChange(payload.new as unknown as BackgroundJob);
      },
    )
    .subscribe();
}
